import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
  TableFooter,
  LinearProgress,
  Alert,
  Chip,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  MdRefresh,
  MdInfoOutline,
  MdFileDownload,
  MdGridOn,
  MdPrint,
  MdReceipt,
  MdAccountBalance,
  MdPercent,
  MdCallSplit,
  MdAssignmentReturn,
  MdVerified,
  MdLocalShipping,
} from 'react-icons/md';
import * as XLSX from 'xlsx';
import { getData } from '../../Webservice/Webservice';
import {
  FONT,
  inr,
  fmtDate,
  toISO,
  authToken,
  rangeLabel,
  StatCard,
  statGridSx,
  exportButtonSx,
  downloadCsv,
  openPrintView,
} from './reportShared';

const REPORT_ROUTE = 'reports/gst-summary';

// Indian GST return periods: months, quarters (Apr–Jun = Q1) and financial year (Apr–Mar).
const periodRange = (key) => {
  const t = new Date();
  const y = t.getFullYear();
  const m = t.getMonth();
  const qStart = Math.floor(((m + 9) % 12) / 3) * 3; // months since April, snapped to quarter
  const qStartMonth = (qStart + 3) % 12;
  const qStartYear = m < 3 && qStartMonth >= 3 ? y - 1 : y;
  const fyStartYear = m >= 3 ? y : y - 1;
  switch (key) {
    case 'this_month':
      return { from: toISO(new Date(y, m, 1)), to: toISO(t) };
    case 'last_month':
      return { from: toISO(new Date(y, m - 1, 1)), to: toISO(new Date(y, m, 0)) };
    case 'this_quarter':
      return { from: toISO(new Date(qStartYear, qStartMonth, 1)), to: toISO(t) };
    case 'last_quarter': {
      const start = new Date(qStartYear, qStartMonth - 3, 1);
      return { from: toISO(start), to: toISO(new Date(qStartYear, qStartMonth, 0)) };
    }
    case 'this_fy':
      return { from: toISO(new Date(fyStartYear, 3, 1)), to: toISO(t) };
    case 'last_fy':
      return { from: toISO(new Date(fyStartYear - 1, 3, 1)), to: toISO(new Date(fyStartYear, 2, 31)) };
    default:
      return { from: '', to: '' };
  }
};

const PERIODS = [
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'this_quarter', label: 'This quarter' },
  { key: 'last_quarter', label: 'Last quarter' },
  { key: 'this_fy', label: 'This FY' },
  { key: 'last_fy', label: 'Last FY' },
];

const pct = (v) => `${v}%`;
const M = { numeric: true, money: true };

const SECTIONS = [
  {
    key: 'hsn',
    label: 'HSN Summary',
    gstr: 'GSTR-1 Table 12',
    columns: [
      { key: 'hsn', label: 'HSN' },
      { key: 'description', label: 'Description' },
      { key: 'uqc', label: 'UQC', center: true },
      { key: 'quantity', label: 'Total Qty', numeric: true, total: true },
      { key: 'rate', label: 'Rate', numeric: true, format: pct },
      { key: 'totalValue', label: 'Total Value', ...M, total: true },
      { key: 'taxableValue', label: 'Taxable Value', ...M, total: true },
      { key: 'igst', label: 'IGST', ...M, total: true },
      { key: 'cgst', label: 'CGST', ...M, total: true },
      { key: 'sgst', label: 'SGST', ...M, total: true },
      { key: 'totalTax', label: 'Total Tax', ...M, total: true },
    ],
  },
  {
    key: 'rateWise',
    label: 'Rate-wise Summary',
    gstr: 'GSTR-3B 3.1(a)',
    columns: [
      { key: 'rate', label: 'GST Rate', format: pct },
      { key: 'totalValue', label: 'Total Value', ...M, total: true },
      { key: 'taxableValue', label: 'Taxable Value', ...M, total: true },
      { key: 'igst', label: 'IGST', ...M, total: true },
      { key: 'cgst', label: 'CGST', ...M, total: true },
      { key: 'sgst', label: 'SGST', ...M, total: true },
      { key: 'totalTax', label: 'Total Tax', ...M, total: true },
    ],
  },
  {
    key: 'b2b',
    label: 'B2B Invoices',
    gstr: 'GSTR-1 Table 4',
    columns: [
      { key: 'gstin', label: 'GSTIN of Recipient', mono: true },
      { key: 'receiverName', label: 'Receiver Name' },
      { key: 'invoiceNumber', label: 'Invoice No.' },
      { key: 'invoiceDate', label: 'Invoice Date', format: fmtDate },
      { key: 'invoiceValue', label: 'Invoice Value', ...M },
      { key: 'placeOfSupply', label: 'Place of Supply' },
      { key: 'reverseCharge', label: 'Reverse Charge', center: true },
      { key: 'invoiceType', label: 'Invoice Type' },
      { key: 'rate', label: 'Rate', numeric: true, format: pct },
      { key: 'taxableValue', label: 'Taxable Value', ...M, total: true },
      { key: 'igst', label: 'IGST', ...M, total: true },
      { key: 'cgst', label: 'CGST', ...M, total: true },
      { key: 'sgst', label: 'SGST', ...M, total: true },
    ],
  },
  {
    key: 'b2cl',
    label: 'B2C Large',
    gstr: 'GSTR-1 Table 5',
    columns: [
      { key: 'invoiceNumber', label: 'Invoice No.' },
      { key: 'invoiceDate', label: 'Invoice Date', format: fmtDate },
      { key: 'invoiceValue', label: 'Invoice Value', ...M },
      { key: 'placeOfSupply', label: 'Place of Supply' },
      { key: 'rate', label: 'Rate', numeric: true, format: pct },
      { key: 'taxableValue', label: 'Taxable Value', ...M, total: true },
      { key: 'igst', label: 'IGST', ...M, total: true },
    ],
  },
  {
    key: 'b2cs',
    label: 'B2C Small',
    gstr: 'GSTR-1 Table 7',
    columns: [
      { key: 'type', label: 'Type', center: true },
      { key: 'supplyType', label: 'Supply Type' },
      { key: 'placeOfSupply', label: 'Place of Supply' },
      { key: 'rate', label: 'Rate', numeric: true, format: pct },
      { key: 'taxableValue', label: 'Taxable Value', ...M, total: true },
      { key: 'igst', label: 'IGST', ...M, total: true },
      { key: 'cgst', label: 'CGST', ...M, total: true },
      { key: 'sgst', label: 'SGST', ...M, total: true },
    ],
  },
  {
    key: 'creditNotes',
    label: 'Credit Notes',
    gstr: 'GSTR-1 Table 9B',
    columns: [
      { key: 'creditNoteNumber', label: 'Credit Note No.' },
      { key: 'creditNoteDate', label: 'Credit Note Date', format: fmtDate },
      { key: 'invoiceNumber', label: 'Original Invoice No.' },
      { key: 'invoiceDate', label: 'Original Invoice Date', format: fmtDate },
      { key: 'receiverName', label: 'Receiver' },
      { key: 'gstin', label: 'GSTIN', mono: true },
      { key: 'placeOfSupply', label: 'Place of Supply' },
      { key: 'hsn', label: 'HSN' },
      { key: 'quantity', label: 'Qty', numeric: true, total: true },
      { key: 'rate', label: 'Rate', numeric: true, format: pct },
      { key: 'taxableValue', label: 'Taxable Value', ...M, total: true },
      { key: 'igst', label: 'IGST', ...M, total: true },
      { key: 'cgst', label: 'CGST', ...M, total: true },
      { key: 'sgst', label: 'SGST', ...M, total: true },
    ],
  },
  {
    key: 'documents',
    label: 'Documents Issued',
    gstr: 'GSTR-1 Table 13',
    columns: [
      { key: 'nature', label: 'Nature of Document' },
      { key: 'fromNumber', label: 'Sr. No. From' },
      { key: 'toNumber', label: 'Sr. No. To' },
      { key: 'totalIssued', label: 'Total Number', numeric: true },
      { key: 'cancelled', label: 'Cancelled', numeric: true },
      { key: 'netIssued', label: 'Net Issued', numeric: true },
    ],
  },
];

const cellValue = (c, r) => {
  const v = r[c.key];
  if (c.money) return inr(v);
  if (c.format) return c.format(v);
  return v === '' || v === null || v === undefined ? '-' : v;
};

const exportValue = (c, r) => {
  const v = r[c.key];
  if (c.key.toLowerCase().includes('date')) return fmtDate(v);
  return v ?? '';
};

const columnTotals = (section, rows) =>
  Object.fromEntries(section.columns.filter((c) => c.total).map((c) => [c.key, Math.round(rows.reduce((a, r) => a + (Number(r[c.key]) || 0), 0) * 100) / 100]));

const GstSummaryReport = () => {
  const initial = periodRange('this_month');
  const [period, setPeriod] = useState('this_month');
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [tab, setTab] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [notesOpen, setNotesOpen] = useState(false);

  const params = useMemo(() => {
    const p = {};
    if (from) p.from = from;
    if (to) p.to = to;
    return p;
  }, [from, to]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getData(REPORT_ROUTE, params, authToken(), false);
      if (res?.success) setData(res);
      else setError(res?.message || 'Failed to load report');
    } catch (err) {
      setError(err.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  useEffect(() => setPage(0), [tab, from, to]);

  const applyPeriod = (key) => {
    const r = periodRange(key);
    setPeriod(key);
    setFrom(r.from);
    setTo(r.to);
  };

  const onMonthPick = (e) => {
    const [y, m] = e.target.value.split('-').map(Number);
    if (!y || !m) return;
    setPeriod('');
    setFrom(toISO(new Date(y, m - 1, 1)));
    setTo(toISO(new Date(y, m, 0)));
  };

  const onDateChange = (setter) => (e) => {
    setter(e.target.value);
    setPeriod('');
  };

  const stats = data?.stats || {};
  const section = SECTIONS[tab];
  const sectionRows = data?.sections?.[section.key] || [];
  const pageRows = sectionRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const totals = columnTotals(section, sectionRows);
  const hasTotals = Object.keys(totals).length > 0 && sectionRows.length > 0;
  const periodText = rangeLabel(from, to);
  const fileStamp = (suffix) => `GST_${suffix}_${from || 'all'}_${to || toISO(new Date())}`;

  const sectionObjects = (s, rows) => {
    const objs = rows.map((r) => Object.fromEntries(s.columns.map((c) => [c.label, exportValue(c, r)])));
    const t = columnTotals(s, rows);
    if (Object.keys(t).length && rows.length) {
      objs.push(Object.fromEntries(s.columns.map((c, i) => [c.label, i === 0 ? 'TOTAL' : t[c.key] ?? ''])));
    }
    return objs;
  };

  const exportExcelWorkbook = () => {
    if (!data) return;
    const book = XLSX.utils.book_new();
    const summary = [
      ['GST Summary Report'],
      ['Return period', periodText],
      ['Company GSTIN', ''],
      [],
      ['Particulars', 'Amount (₹)'],
      ['Invoices issued (net)', stats.invoices],
      ['B2B invoices', stats.b2bInvoices],
      ['B2C invoices', stats.b2cInvoices],
      ['Total invoice value', stats.invoiceValue],
      ['Taxable value', stats.taxableValue],
      ['IGST', stats.igst],
      ['CGST', stats.cgst],
      ['SGST', stats.sgst],
      ['Total output tax', stats.totalTax],
      ['Credit notes (count)', stats.creditNotes],
      ['Credit note taxable value', stats.creditNoteTaxable],
      ['Credit note tax', stats.creditNoteTax],
      ['Net taxable value', stats.netTaxable],
      ['Net tax liability', stats.netTax],
      ['Delivery charges collected (not taxed at checkout)', stats.deliveryCharges],
      [],
      ['Notes'],
      ...(data.notes || []).map((n) => [n]),
    ];
    const summarySheet = XLSX.utils.aoa_to_sheet(summary);
    summarySheet['!cols'] = [{ wch: 48 }, { wch: 22 }];
    XLSX.utils.book_append_sheet(book, summarySheet, 'Summary');
    for (const s of SECTIONS) {
      const objs = sectionObjects(s, data.sections?.[s.key] || []);
      const sheet = objs.length ? XLSX.utils.json_to_sheet(objs) : XLSX.utils.aoa_to_sheet([s.columns.map((c) => c.label)]);
      sheet['!cols'] = s.columns.map(() => ({ wch: 18 }));
      XLSX.utils.book_append_sheet(book, sheet, s.label.slice(0, 31));
    }
    XLSX.writeFile(book, `${fileStamp('Summary')}.xlsx`);
  };

  const runExport = (kind) => {
    setExporting(true);
    try {
      if (kind === 'excel') exportExcelWorkbook();
      else if (kind === 'csv') downloadCsv(sectionObjects(section, sectionRows), fileStamp(section.label.replace(/\s+/g, '_')));
      else {
        const cells = sectionRows.map((r) => section.columns.map((c) => ({ value: cellValue(c, r), className: c.numeric ? 'num' : '' })));
        if (hasTotals) cells.push(section.columns.map((c, i) => ({ value: i === 0 ? 'TOTAL' : c.key in totals ? (c.money ? inr(totals[c.key]) : totals[c.key]) : '', className: c.numeric ? 'num' : '' })));
        openPrintView({
          title: `GST Summary — ${section.label} (${section.gstr})`,
          subtitle: `Return period: ${periodText}`,
          headers: section.columns.map((c) => c.label),
          cells,
        });
      }
    } catch (err) {
      setError(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const cards = [
    { label: 'Invoices (net)', value: (stats.invoices || 0).toLocaleString('en-IN'), icon: MdReceipt, gradient: 'linear-gradient(135deg,#6366f1,#8b5cf6)', sub: `B2B ${stats.b2bInvoices || 0} · B2C ${stats.b2cInvoices || 0}` },
    { label: 'Taxable Value', value: inr(stats.taxableValue), icon: MdAccountBalance, gradient: 'linear-gradient(135deg,#0ea5e9,#2563eb)', sub: `Invoice value ${inr(stats.invoiceValue)}` },
    { label: 'Output Tax', value: inr(stats.totalTax), icon: MdPercent, gradient: 'linear-gradient(135deg,#f59e0b,#ea580c)', sub: 'IGST + CGST + SGST' },
    { label: 'CGST + SGST (intra-state)', value: inr((stats.cgst || 0) + (stats.sgst || 0)), icon: MdCallSplit, gradient: 'linear-gradient(135deg,#a855f7,#7e22ce)', sub: `CGST ${inr(stats.cgst)} · SGST ${inr(stats.sgst)}` },
    { label: 'IGST (inter-state)', value: inr(stats.igst), icon: MdCallSplit, gradient: 'linear-gradient(135deg,#ec4899,#db2777)', sub: stats.igst ? 'Sales shipped to other states' : 'No inter-state sales in this period' },
    { label: 'Credit Notes', value: inr(stats.creditNoteTax), icon: MdAssignmentReturn, gradient: 'linear-gradient(135deg,#f43f5e,#dc2626)', sub: `${stats.creditNotes || 0} notes · taxable ${inr(stats.creditNoteTaxable)}` },
    { label: 'Net Tax Liability', value: inr(stats.netTax), icon: MdVerified, gradient: 'linear-gradient(135deg,#10b981,#059669)', sub: `Net taxable ${inr(stats.netTaxable)}` },
    { label: 'Delivery Charges', value: inr(stats.deliveryCharges), icon: MdLocalShipping, gradient: 'linear-gradient(135deg,#0f172a,#334155)', sub: 'Collected, not taxed at checkout' },
  ];

  const warnings = [];
  if (stats.missingWarehouseState) warnings.push(`${stats.missingWarehouseState} invoice(s) are from a warehouse with no state set — they were treated as intra-state. Set the warehouse state for an accurate IGST/CGST split.`);
  if (stats.unmappedLines) warnings.push(`${stats.unmappedLines} line item(s) have no HSN/rate (combos or deleted products) and are shown under HSN "UNMAPPED".`);

  return (
    <Box sx={{ p: { xs: 1, md: 3 }, fontFamily: FONT }}>
      {/* Header */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box>
          <Typography sx={{ fontSize: 23.5, fontWeight: 800, fontFamily: FONT, color: '#0f172a' }}>GST Summary Report</Typography>
          <Typography sx={{ fontSize: 12.5, color: '#64748b', fontFamily: FONT }}>
            Return period: <b>{periodText}</b> · structured for GSTR-1 / GSTR-3B filing
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button variant="outlined" startIcon={<MdInfoOutline />} onClick={() => setNotesOpen(true)} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px', fontFamily: FONT }}>
            Note
          </Button>
          <Button startIcon={<MdGridOn />} disabled={exporting || !data} onClick={() => runExport('excel')} sx={exportButtonSx('#0d9488', '#0f766e')}>
            Excel (all sections)
          </Button>
          <Button startIcon={<MdFileDownload />} disabled={exporting || !sectionRows.length} onClick={() => runExport('csv')} sx={exportButtonSx('#16a34a', '#15803d')}>
            CSV (this tab)
          </Button>
          <Button startIcon={<MdPrint />} disabled={exporting || !sectionRows.length} onClick={() => runExport('print')} sx={exportButtonSx('#7c3aed', '#6d28d9')}>
            Print / PDF
          </Button>
        </Box>
      </Box>

      <Box sx={statGridSx}>
        {cards.map((c) => (
          <StatCard key={c.label} {...c} />
        ))}
      </Box>

      {/* Return period */}
      <Paper elevation={0} sx={{ p: 2.5, borderRadius: '16px', mb: 3, border: '1px solid #e2e8f0' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography sx={{ fontWeight: 800, fontFamily: FONT, color: '#0f172a' }}>Return Period</Typography>
          <Button size="small" startIcon={<MdRefresh />} onClick={() => applyPeriod('this_month')} sx={{ textTransform: 'none', fontWeight: 700, color: '#dc2626' }}>
            Reset
          </Button>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {PERIODS.map((p) => (
            <Chip
              key={p.key}
              label={p.label}
              onClick={() => applyPeriod(p.key)}
              sx={{
                fontWeight: 700,
                fontFamily: FONT,
                background: period === p.key ? 'rgb(255 209 2)' : '#f1f5f9',
                color: '#0f172a',
                border: period === p.key ? '1px solid #0f172a' : '1px solid transparent',
                '&:hover': { background: period === p.key ? 'rgb(255 209 2)' : '#e2e8f0' },
              }}
            />
          ))}
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4} md={3}>
            <TextField label="Filing month" type="month" size="small" fullWidth InputLabelProps={{ shrink: true }} value={from ? from.slice(0, 7) : ''} onChange={onMonthPick} />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <TextField label="From" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={from} onChange={onDateChange(setFrom)} inputProps={{ max: to || undefined }} />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <TextField label="To" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={to} onChange={onDateChange(setTo)} inputProps={{ min: from || undefined }} />
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {warnings.map((w) => (
        <Alert key={w} severity="warning" sx={{ mb: 1.5, fontFamily: FONT }}>{w}</Alert>
      ))}

      {/* Sections */}
      <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: 1,
            borderBottom: '1px solid #e2e8f0',
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, fontFamily: FONT, minHeight: 56 },
            '& .Mui-selected': { color: '#0f172a !important' },
            '& .MuiTabs-indicator': { background: 'rgb(255 209 2)', height: 4, borderRadius: 2 },
          }}
        >
          {SECTIONS.map((s) => (
            <Tab
              key={s.key}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  {s.label}
                  <Chip size="small" label={(data?.sections?.[s.key] || []).length} sx={{ height: 20, fontSize: 11, fontWeight: 800 }} />
                </Box>
              }
            />
          ))}
        </Tabs>
        <Box sx={{ px: 2.5, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          <Typography sx={{ fontWeight: 800, fontFamily: FONT, color: '#0f172a' }}>{section.label}</Typography>
          <Chip size="small" label={section.gstr} sx={{ fontWeight: 700, background: '#e0e7ff', color: '#3730a3' }} />
        </Box>
        {(loading || exporting) && <LinearProgress />}
        <TableContainer sx={{ maxHeight: 560 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {section.columns.map((c) => (
                  <TableCell
                    key={c.key}
                    align={c.center ? 'center' : c.numeric ? 'right' : 'left'}
                    sx={{ fontWeight: 800, fontFamily: FONT, background: '#0f172a', color: '#fff', whiteSpace: 'nowrap', py: 1.5 }}
                  >
                    {c.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && sectionRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={section.columns.length} sx={{ textAlign: 'center', color: '#94a3b8', py: 6, fontFamily: FONT }}>
                    Nothing to report in this section for the selected period.
                  </TableCell>
                </TableRow>
              )}
              {pageRows.map((r, idx) => (
                <TableRow key={idx} hover sx={{ '&:nth-of-type(even)': { background: '#f8fafc' } }}>
                  {section.columns.map((c) => (
                    <TableCell
                      key={c.key}
                      align={c.center ? 'center' : c.numeric ? 'right' : 'left'}
                      sx={{
                        fontFamily: c.mono ? 'monospace' : FONT,
                        whiteSpace: 'nowrap',
                        fontSize: 11.5,
                        fontWeight: c.key === 'totalTax' || c.key === 'hsn' ? 700 : 500,
                        color: r.hsn === 'UNMAPPED' && c.key === 'hsn' ? '#b45309' : '#334155',
                      }}
                    >
                      {cellValue(c, r)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
            {hasTotals && (
              <TableFooter>
                <TableRow sx={{ background: '#fef9c3' }}>
                  {section.columns.map((c, i) => (
                    <TableCell
                      key={c.key}
                      align={c.center ? 'center' : c.numeric ? 'right' : 'left'}
                      sx={{ fontWeight: 800, fontFamily: FONT, color: '#0f172a', fontSize: 11.5, whiteSpace: 'nowrap', position: 'sticky', bottom: 0, background: '#fef9c3' }}
                    >
                      {i === 0 ? 'Total' : c.key in totals ? (c.money ? inr(totals[c.key]) : totals[c.key].toLocaleString('en-IN')) : ''}
                    </TableCell>
                  ))}
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={sectionRows.length}
          page={Math.min(page, Math.max(Math.ceil(sectionRows.length / rowsPerPage) - 1, 0))}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid #e2e8f0' }}
        />
      </Paper>

      <Dialog open={notesOpen} onClose={() => setNotesOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, fontFamily: FONT }}>Note</DialogTitle>
        <DialogContent dividers>
          <Box component="ul" sx={{ pl: 2, m: 0 }}>
            {(data?.notes || []).map((n, i) => (
              <Typography component="li" key={i} variant="body2" sx={{ mb: 1, fontFamily: FONT }}>{n}</Typography>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotesOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GstSummaryReport;
